import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  DECIMAL_RE,
  defaultDiamondFields,
  handleDecimalKeyDown,
  handleDecimalPaste,
  handleIntegerBeforeInput,
  handleIntegerKeyDown,
  handleIntegerPaste,
} from "../../config/fieldsHelper";
import { useGetAllDiamondsQuery } from "../../toolkitServices/diamondApi";
import { useGetDiamondSievesQuery } from "../../toolkitServices/diamondSieveApi";
import { useGetDiamondSizesQuery } from "../../toolkitServices/diamondSizeApi";

const DiamondPopUpNew = ({
  initialDiamonds = [],
  setOpenDiamondModal,
  onSave,
  itemIndex = 0,
}) => {
  // 🔹 API Data
  const { data: diamondsRes } = useGetAllDiamondsQuery();
  const { data: sieveRes } = useGetDiamondSievesQuery({ page: 1, limit: 500 });
  const { data: sizeRes } = useGetDiamondSizesQuery({ page: 1, limit: 500 });

  // 🔹 Dropdown data
  const diamondsData = useMemo(
    () =>
      (diamondsRes?.data || []).map((d) => ({
        value: d._id,
        label: d.diamondName || d.name || "Unnamed",
        info: d,
      })),
    [diamondsRes]
  );

  // 🔹 Form setup
  const { register, control, handleSubmit, setValue, getValues, setFocus, watch, reset } =
    useForm({
      defaultValues: {
        diamonds: initialDiamonds?.length ? initialDiamonds : [defaultDiamondFields],
      },
    });

  useEffect(() => {
    reset({ diamonds: initialDiamonds?.length ? initialDiamonds : [defaultDiamondFields] });
  }, [initialDiamonds, reset]);

  const { fields, append, remove } = useFieldArray({ control, name: "diamonds" });

  const scrollRef = useRef(null);
  const rowRefs = useRef([]);

  // 🔹 Focus first row automatically when popup opens
  useEffect(() => {
    setTimeout(() => {
      setFocus("diamonds.0.diaPopupName");
      rowRefs.current[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }, [setFocus]);

  // 🔹 Shape-based dropdowns
  const [sieveOptionsByShape, setSieveOptionsByShape] = useState({});
  const [sizeOptionsByShape, setSizeOptionsByShape] = useState({});

  useEffect(() => {
    if (sieveRes?.data?.length) {
      const grouped = sieveRes.data.reduce((acc, s) => {
        const shapeId = s?.shape?._id;
        if (!acc[shapeId]) acc[shapeId] = [];
        acc[shapeId].push({ value: s._id, label: s.sieve || s.name || "Unnamed" });
        return acc;
      }, {});
      setSieveOptionsByShape(grouped);
    }
  }, [sieveRes]);

  useEffect(() => {
    if (sizeRes?.data?.length) {
      const grouped = sizeRes.data.reduce((acc, s) => {
        const shapeId = s?.shape?._id;
        if (!acc[shapeId]) acc[shapeId] = [];
        acc[shapeId].push({ value: s._id, label: s.size || s.name || "Unnamed" });
        return acc;
      }, {});
      setSizeOptionsByShape(grouped);
    }
  }, [sizeRes]);

  // 🔹 Amount calculation
  const recomputeAmt = (i) => {
    const wt = parseFloat(getValues(`diamonds.${i}.diaPopupWt`));
    const rWt = parseFloat(getValues(`diamonds.${i}.diaPopupRateByWt`));
    const pcs = parseInt(getValues(`diamonds.${i}.diaPopupPcs`), 10);
    const rPc = parseFloat(getValues(`diamonds.${i}.diaPopupRateByPcs`));
    let amt = "";
    if (!isNaN(wt) && !isNaN(rWt) && (isNaN(pcs) || pcs === 0)) {
      amt = (wt * rWt).toFixed(2);
    } else if (!isNaN(pcs) && !isNaN(rPc)) {
      amt = (pcs * rPc).toFixed(2);
    }
    setValue(`diamonds.${i}.diaPopupAmt`, amt, { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveClick = (data) => {
    onSave?.(data?.diamonds);
    setOpenDiamondModal(false);
  };

  // 🔹 Scroll helper
  const scrollRowIntoView = (index) => {
    const el = rowRefs.current[index];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // 🔹 Live totals (instant on change)
  const diamondWatchAll = watch("diamonds");
  const totals = {
    totalWt: diamondWatchAll
      ?.reduce((sum, d) => sum + parseFloat(d?.diaPopupWt || 0), 0)
      .toFixed(3),
    totalPcs: diamondWatchAll
      ?.reduce((sum, d) => sum + parseFloat(d?.diaPopupPcs || 0), 0)
      .toFixed(0),
    totalAmt: diamondWatchAll
      ?.reduce((sum, d) => sum + parseFloat(d?.diaPopupAmt || 0), 0)
      .toFixed(2),
  };

  // ============================================================
  // ======================= UI PART ============================
  // ============================================================
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-[#f7f4ff] border border-violet-300 rounded-lg shadow-xl w-[80vw] max-w-4xl max-h-[260px] flex flex-col relative"
        >
          {/* === Sticky Header === */}
          <div className="sticky top-0 bg-[#f7f4ff] z-20 border-b border-violet-200 p-2 flex justify-between items-center">
            <h5 className="text-sm font-semibold text-violet-700 flex items-center gap-2">
              💎 Diamonds — <span className="text-gray-600">Item #{itemIndex + 1}</span>
            </h5>
            <button
              type="button"
              onClick={() => setOpenDiamondModal(false)}
              className="text-xs text-gray-500 hover:text-red-600 transition"
            >
              ✕ Close
            </button>
          </div>

          {/* === Scrollable Content === */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
            {fields.map((field, idx) => {
              const unit = watch(`diamonds.${idx}.unit`);
              const shape = watch(`diamonds.${idx}.diaPopupShape`);
              const hasWt = !!watch(`diamonds.${idx}.diaPopupWt`);
              const hasRateWt = !!watch(`diamonds.${idx}.diaPopupRateByWt`);
              const hasPcs = !!watch(`diamonds.${idx}.diaPopupPcs`);
              const hasRatePcs = !!watch(`diamonds.${idx}.diaPopupRateByPcs`);
              const weightMode = hasWt || hasRateWt;
              const pcsMode = hasPcs || hasRatePcs;
              const currentShapeId = typeof shape === "object" ? shape?.value : shape;
              const sieveOptions = sieveOptionsByShape[currentShapeId] || [];
              const sizeOptions = sizeOptionsByShape[currentShapeId] || [];

              return (
                <div
                  key={field.id}
                  ref={(el) => (rowRefs.current[idx] = el)}
                  className="border border-violet-200 bg-white rounded-md p-2 shadow-sm relative"
                >
                  {/* Sr No */}
                  <div className="absolute -left-2 -top-2 bg-violet-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center shadow">
                    {idx + 1}
                  </div>

                  {/* === First Line === */}
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {/* Name */}
                    <div>
                      <label className="text-[10px] text-gray-600">Name</label>
                      <select
                        {...register(`diamonds.${idx}.diaPopupName`, {
                          onChange: (e) => {
                            const id = e.target.value;
                            const sel = diamondsData.find((s) => String(s.value) === String(id));
                            if (!sel) return;
                            setValue(`diamonds.${idx}.unit`, { value: "carat", label: "carat" });
                            setValue(`diamonds.${idx}.diaPopupShape`, sel.info?.shape?._id || "");
                            setValue(`diamonds.${idx}.diaPopupColor`, sel.info?.color || "");
                            setValue(`diamonds.${idx}.diaPopupClarity`, sel.info?.clarity || "");
                            setValue(`diamonds.${idx}.diaPopupDescription`, sel.info?.description ?? "");
                          },
                        })}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300 focus:ring-2 focus:ring-violet-300 outline-none"
                      >
                        <option value="">--Select--</option>
                        {diamondsData.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Sieve */}
                    <div>
                      <label className="text-[10px] text-gray-600">Sieve</label>
                      <select
                        {...register(`diamonds.${idx}.diaPopupSieve`)}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300 focus:ring-2 focus:ring-violet-300 outline-none"
                      >
                        <option value="">--Select--</option>
                        {sieveOptions.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Size */}
                    <div>
                      <label className="text-[10px] text-gray-600">Size</label>
                      <select
                        {...register(`diamonds.${idx}.diaPopupSize`)}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300 focus:ring-2 focus:ring-violet-300 outline-none"
                      >
                        <option value="">--Select--</option>
                        {sizeOptions.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Weight */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Wt {unit?.value === "gram" ? "(gm)" : "(ct)"}
                      </label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) =>
                            v === "" || DECIMAL_RE.test(String(v)) || "Only numbers/decimal",
                        })}
                        placeholder="Wt"
                        inputMode="decimal"
                        autoComplete="off"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`diamonds.${idx}.diaPopupWt`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Wt */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Rate/Wt {unit?.value === "gram" ? "(gm)" : "(ct)"}
                      </label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupRateByWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) =>
                            v === "" || DECIMAL_RE.test(String(v)) || "Only numbers/decimal",
                        })}
                        placeholder="Rate/Wt"
                        inputMode="decimal"
                        autoComplete="off"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`diamonds.${idx}.diaPopupRateByWt`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Pcs</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        {...register(`diamonds.${idx}.diaPopupPcs`, {
                          onChange: () => recomputeAmt(idx),
                          setValueAs: (v) => (v === "" ? "" : String(v).replace(/\D/g, "")),
                        })}
                        placeholder="Pcs"
                        onBeforeInput={handleIntegerBeforeInput}
                        onKeyDown={handleIntegerKeyDown}
                        onPaste={handleIntegerPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Rate/Pcs</label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupRateByPcs`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) =>
                            v === "" || DECIMAL_RE.test(String(v)) || "Only numbers/decimal",
                        })}
                        placeholder="Rate/Pcs"
                        inputMode="decimal"
                        autoComplete="off"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`diamonds.${idx}.diaPopupRateByPcs`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>
                  </div>

                  {/* === Second Line === */}
                  <div className="grid grid-cols-5 gap-2 items-end">
                    <div>
                      <label className="text-[10px] text-gray-600">Amt</label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupAmt`)}
                        placeholder="0.00"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] text-gray-600">Deduct</label>
                      <select
                        {...register(`diamonds.${idx}.diaPopupDeduct`)}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300 focus:ring-2 focus:ring-violet-300 outline-none"
                      >
                        <option value="">--Select--</option>
                        <option value="deduct">Deduct</option>
                        <option value="add">Add</option>
                        <option value="nonDeduct">Non-Deduct</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="text-[10px] text-gray-600">Description</label>
                      <input
                        {...register(`diamonds.${idx}.diaPopupDescription`)}
                        placeholder="Description"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-center">
                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => {
                          const prevIndex = idx - 1 >= 0 ? idx - 1 : 0;
                          remove(idx);
                          setTimeout(() => {
                            setFocus(`diamonds.${prevIndex}.diaPopupName`);
                            scrollRowIntoView(prevIndex);
                          }, 150);
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-red-50"
                      >
                        <FaTrash size={10} className="text-gray-500" />
                      </button>

                      {/* Add */}
                      <button
                        type="button"
                        onClick={() => {
                          const newIndex = fields.length;
                          append(defaultDiamondFields);
                          requestAnimationFrame(() => {
                            setTimeout(() => {
                              setFocus(`diamonds.${newIndex}.diaPopupName`);
                              scrollRowIntoView(newIndex);
                            }, 300); // 🟢 Increased delay for stable focus + scroll
                          });
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-green-50"
                      >
                        <FaPlus size={10} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* === Totals Chip === */}
          <div className="sticky bottom-8 flex justify-center mt-1">
            <div className="bg-violet-100 border border-violet-300 rounded-full text-[11px] px-4 py-1 shadow-md text-violet-700 flex gap-4 transition-all duration-150">
              <span>
                💎 Total Wt: <strong>{totals.totalWt}</strong>
              </span>
              <span>
                🔹 Total Pcs: <strong>{totals.totalPcs}</strong>
              </span>
              <span>
                💰 Total Amt: <strong>{totals.totalAmt}</strong>
              </span>
            </div>
          </div>

          {/* === Footer === */}
          <div className="sticky bottom-0 bg-[#f7f4ff] z-20 border-t border-violet-200 p-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpenDiamondModal(false)}
              className="text-xs px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(handleSaveClick)}
              className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
            >
              Save
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DiamondPopUpNew;
